import React, {
  useCallback, useContext, useState
} from 'react';
import { useNavigate } from 'react-router-dom';

import { ButtonBar, ScreenHeader } from '../../components';
import { useScrollToTop } from '../../hooks';

import { NewPOAContext } from './new-poa-composer';

export function NewPOAGiftsToAgent() {
  const { powerOfAttorney, setPowerOfAttorney } = useContext(NewPOAContext);
  const navigate = useNavigate();
  const [ giftsToAgent, setGiftsToAgent ] = useState(powerOfAttorney.gifts_to_agent || false);
  const [ maxGiftAmount, setMaxGiftAmount ] = useState(powerOfAttorney.max_gift_amount_per_year || '');
  
  useScrollToTop();
  
  const handleBack = useCallback(() => {
    navigate('../gifts-intro');
  }, [ navigate ]);

  const handleNext = useCallback(() => {
    setPowerOfAttorney(prev => ({ 
      ...prev, 
      gifts_to_agent: giftsToAgent,
      max_gift_amount_per_year: giftsToAgent ? maxGiftAmount : ''
    }));
    navigate('../gifts-to-others');
  }, [ giftsToAgent, maxGiftAmount, setPowerOfAttorney, navigate ]);
  
  return (
    <div className="new-poa-gifts-to-agent screen-container screen">
      <ScreenHeader
        title="Gifts to Agent"
        description="Decide whether to authorize your agent to make gifts to themselves"
        handleBack={handleBack}
      />
      
      <div className="content-section">
        <h3>Gifts to Your Agent</h3>
        <p>
          You can authorize your agent to make gifts to themselves, such as for 
          holidays, birthdays, or other special occasions. This might include:
        </p>
        <ul className="gift-examples">
          <li>Holiday and birthday gifts</li>
          <li>Anniversary or special occasion gifts</li>
          <li>Gifts similar to what you've given in the past</li>
          <li>Reasonable gifts appropriate to the relationship</li>
        </ul>

        <h3>Do You Want to Authorize Gifts to Your Agent?</h3>
        <div className="gift-authorization">
          <div className="gift-option">
            <label className="gift-label">
              <input
                type="radio"
                name="gifts-to-agent"
                checked={!giftsToAgent}
                onChange={() => setGiftsToAgent(false)}
              />
              <div className="gift-content">
                <h4>No</h4>
                <p>Do not authorize gifts to my agent</p>
              </div>
            </label>
          </div>

          <div className="gift-option">
            <label className="gift-label">
              <input
                type="radio"
                name="gifts-to-agent"
                checked={giftsToAgent}
                onChange={() => setGiftsToAgent(true)}
              />
              <div className="gift-content">
                <h4>Yes</h4>
                <p>Authorize gifts to my agent with limitations</p>
              </div>
            </label>
          </div>
        </div>

        {giftsToAgent && (
          <div className="gift-amount-section">
            <h3>Maximum Gift Amount Per Year</h3>
            <div className="amount-input-group">
              <label htmlFor="max-gift-amount" className="amount-label">
                Maximum amount per year (optional)
              </label>
              <div className="amount-input-wrapper">
                <span className="currency-symbol">$</span>
                <input
                  id="max-gift-amount"
                  type="number"
                  value={maxGiftAmount}
                  onChange={(e) => setMaxGiftAmount(e.target.value)}
                  className="amount-input"
                  placeholder="0"
                  min="0"
                  step="100"
                />
              </div>
              <p className="amount-help">
                Leave blank for no specific limit. Consider the annual gift tax exclusion limit.
              </p>
            </div>
          </div>
        )}

        <div className="important-note">
          <h4>Important:</h4>
          <p>
            Gifts to your agent may be subject to special scrutiny and should be 
            reasonable and appropriate. Consider setting clear limits to prevent 
            potential conflicts of interest.
          </p>
        </div>
      </div>

      <ButtonBar 
        handlePrimary={handleNext}
        primaryText="Continue"
      />
    </div>
  );
}
